import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import ReactPlayer from "react-player";
import { uploadFile } from "../../../Blogs/others/Uploads/upload.Helper";
import { creatingStates } from "../../../Blogs/Constants";
import UploadBlogsStatus from "../../../../../../Components/UploadBlogsStatus";
import FileUploader from "../../../../../../Components/FileUploader";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  createRecordedVideoApi,
  getRecordedVideoApi,
  updateRecordedVideoApi,
  getAllLiveSessions,
} from "../../Constants/api";

const CreateRecordedSession = () => {
  const history = useHistory();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    Url: "",
    thumbnailImage: "",
    sessionId: "",
    duration_min: "",
    duration_sec: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState("");
  const [thumbnailUploadPercentage, setThumbnailUploadPercentage] = useState(0);
  const [videoUploadPercentage, setVideoUploadPercentage] = useState(0);
  const [creating, setCreating] = useState({
    isCreating: false,
    creatingStage: creatingStates.UPLOADING_IMAGE,
  });
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});
  const initialData = useRef(null);

  // Dropdown States
  const [sessionOptions, setSessionOptions] = useState([]);
  const [minuteOptions] = useState(Array.from({ length: 301 }, (_, i) => ({ label: i.toString(), value: i })));
  const [secondOptions] = useState(Array.from({ length: 60 }, (_, i) => ({ label: i.toString().padStart(2, '0'), value: i })));

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [selectedSecond, setSelectedSecond] = useState(null);

  const { isCreating, creatingStage } = creating;
  const { title, description, Url, thumbnailImage, sessionId } = formData;

  useEffect(() => {
    if (id) {
      fetchSessionDetails(id);
    }
    fetchLiveSessions(1);
  }, [id]);

  const fetchLiveSessions = async (pageNum) => {
    setIsLoadingSessions(true);
    try {
      const { data } = await getAllLiveSessions({ page: pageNum, limit: 10 });
      const newSessions = data?.data || [];

      const options = newSessions.map(session => ({
        label: session.title || session.name || "Untitled Session",
        value: session._id
      }));

      setSessionOptions(prev => {
        if (pageNum === 1) return options;
        return [...prev, ...options];
      });

      if (newSessions.length < 10) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching live sessions:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const { data } = await getRecordedVideoApi(sessionId);
      if (data && data.data) {
        const fetchedData = {
          title: data.data.title || "",
          description: data.data.description || "",
          Url: data.data.Url || "",
          thumbnailImage: data.data.thumbnailImage || "",
          sessionId: data.data.sessionId || "",
        };
        setFormData(fetchedData);

        // Store initial data snapshot for diffing on update
        initialData.current = {
          ...fetchedData,
          duration: data.data.duration || 0,
        };

        if (data.data.duration) {
          const m = Math.floor(data.data.duration / 60);
          const s = data.data.duration % 60;
          setSelectedMinute({ label: m.toString(), value: m });
          setSelectedSecond({ label: s.toString().padStart(2, '0'), value: s });
        }
        setPreviewImage(data.data.thumbnailImage);
        setPreviewVideoUrl(data.data.Url);
      }
    } catch (error) {
      console.error("Error fetching session details:", error);
    }
  };

  useEffect(() => {
    if (videoFile && typeof videoFile !== "string") {
      const url = URL.createObjectURL(videoFile);
      setPreviewVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [videoFile]);

  useEffect(() => {
    // If we have a sessionId from details, try to set the selected option object
    // This is best effort if the session is in the first page or loaded part.
    if (sessionId && sessionOptions.length > 0 && !selectedSession) {
      const found = sessionOptions.find(opt => opt.value === sessionId);
      if (found) setSelectedSession(found);
    }
  }, [sessionId, sessionOptions]);


  const handleScrollToBottom = () => {
    if (hasMore && !isLoadingSessions) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLiveSessions(nextPage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleMinuteChange = (selectedOption) => {
    setSelectedMinute(selectedOption);
    if (errors.duration) setErrors({ ...errors, duration: null });
  };

  const handleSecondChange = (selectedOption) => {
    setSelectedSecond(selectedOption);
    if (errors.duration) setErrors({ ...errors, duration: null });
  };

  const handleSessionChange = (selectedOption) => {
    setSelectedSession(selectedOption);
    setFormData({ ...formData, sessionId: selectedOption ? selectedOption.value : "" });
    if (errors.sessionId) {
      setErrors({ ...errors, sessionId: null });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Allowed file types
      const allowedFormats = ['image/jpeg', 'image/png'];
      const allowedExtensions = ['.jpg', '.jpeg', '.png'];

      // Check file type
      const fileType = file.type.toLowerCase();
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      const isValidType = allowedFormats.includes(fileType);
      const isValidExtension = allowedExtensions.includes(fileExtension);

      if (!isValidType || !isValidExtension) {
        setErrors({
          ...errors,
          thumbnailImage: "Invalid file format. Only JPG, JPEG, and PNG formats are accepted."
        });
        setImageFile(null);
        setPreviewImage("");
        e.target.value = ""; // Reset file input
        return;
      }

      // File is valid
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
      if (errors.thumbnailImage) {
        setErrors({ ...errors, thumbnailImage: null });
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!title.trim()) newErrors.title = "Title is required";
    if (!description.trim()) newErrors.description = "Description is required";

    // Video validation - check for video file or existing URL
    if (!id && !videoFile) {
      newErrors.Url = "Video is required";
    }
    if (id && !videoFile && !Url) {
      newErrors.Url = "Video is required";
    }

    if (!id && !imageFile && !thumbnailImage) newErrors.thumbnailImage = "Thumbnail is required";
    if (id && !imageFile && !thumbnailImage) newErrors.thumbnailImage = "Thumbnail is required";

    if (!sessionId) newErrors.sessionId = "Please select a Live Session";

    if (!selectedMinute && !selectedSecond) {
      newErrors.duration = "Duration is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setCreating({
      isCreating: true,
      creatingStage: creatingStates.UPLOADING_IMAGE,
    });

    try {
      let uploadedImageUrl = thumbnailImage;
      let uploadedVideoUrl = Url;

      if (imageFile) {
        uploadedImageUrl = await uploadFile(
          imageFile,
          "RECORDED-SESSION-THUMBNAIL",
          setThumbnailUploadPercentage,
          20
        );
      }

      // Upload video file if new video is selected
      if (videoFile) {
        setCreating({
          isCreating: true,
          creatingStage: creatingStates.UPLOADING_MUSIC,
        });
        uploadedVideoUrl = await uploadFile(
          videoFile,
          "RECORDED-SESSION-VIDEO",
          setVideoUploadPercentage,
          50
        );
      }

      setCreating({
        isCreating: true,
        creatingStage: creatingStates.CREATING_BLOG,
      });

      const currentDuration = (Number(selectedMinute?.value || 0) * 60) + Number(selectedSecond?.value || 0);

      if (id) {
        // Build payload with only the fields that actually changed
        const changedPayload = {};
        const original = initialData.current || {};

        if (formData.title !== original.title) changedPayload.title = formData.title;
        if (formData.description !== original.description) changedPayload.description = formData.description;
        if (formData.sessionId !== original.sessionId) changedPayload.sessionId = formData.sessionId;
        if (currentDuration !== original.duration) changedPayload.duration = currentDuration;

        // Include thumbnailImage only if a new image was uploaded
        if (imageFile) changedPayload.thumbnailImage = uploadedImageUrl;
        // Include Url only if a new video was uploaded
        if (videoFile) changedPayload.Url = uploadedVideoUrl;

        if (Object.keys(changedPayload).length === 0) {
          setCreating({ isCreating: false, creatingStage: creatingStates.UPLOADING_IMAGE });
          setAlert(
            <SweetAlert
              info
              title="No Changes"
              onConfirm={() => setAlert(null)}
            >
              No fields were modified.
            </SweetAlert>
          );
          return;
        }

        await updateRecordedVideoApi(id, changedPayload);
      } else {
        const payload = {
          ...formData,
          thumbnailImage: uploadedImageUrl,
          Url: uploadedVideoUrl,
          duration: currentDuration,
        };
        await createRecordedVideoApi(payload);
      }

      setCreating({
        isCreating: true,
        creatingStage: creatingStates.UPLOAD_COMPLETE,
      });

      setAlert(
        <SweetAlert
          success
          title={id ? "Updated!" : "Created!"}
          onConfirm={() => {
            setAlert(null);
            history.push("/dashboard/recorded-sessions/all");
          }}
        >
          Session has been {id ? "updated" : "created"} successfully.
        </SweetAlert>
      );

    } catch (error) {
      console.error("Error submitting form:", error);
      setCreating({
        isCreating: false,
        creatingStage: creatingStates.UPLOADING_IMAGE,
      });
      setAlert(
        <SweetAlert
          danger
          title="Error"
          onConfirm={() => setAlert(null)}
        >
          Something went wrong.
        </SweetAlert>
      );
    }
  };

  const reset = () => {
    setCreating({ isCreating: false, creatingStage: creatingStates.UPLOADING_IMAGE });
    setThumbnailUploadPercentage(0);
    setVideoUploadPercentage(0);
    // Reset form for next entry
    setFormData({
      title: "",
      description: "",
      Url: "",
      thumbnailImage: "",
      sessionId: "",
    });
    setSelectedMinute(null);
    setSelectedSecond(null);
    setImageFile(null);
    setPreviewImage("");
    setVideoFile(null);
    setPreviewVideoUrl("");
    setSelectedSession(null);
    setAlert(null);
    // Note: We don't push to history here, so we stay on create page.
  };

  const ErrorMsg = ({ msg }) => (
    msg ? <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>{msg}</div> : null
  );

  return (
    <>
      {isCreating ? (
        <UploadBlogsStatus
          currentStep={creatingStage}
          thumbnailUploadPercentage={thumbnailUploadPercentage}
          audioUploadPercentage={videoUploadPercentage}
          reset={reset}
          fileName="recorded-sessions"
        />
      ) : (
        <div className="card">
          <div className="card-header">
            <h3>{id ? "Edit Recorded Session" : "Create Recorded Session"}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  value={title}
                  onChange={handleChange}
                />
                <ErrorMsg msg={errors.title} />
              </div>

              <div className="form-group">
                <label>Select Live Session <span className="text-danger">*</span></label>
                <Select
                  options={sessionOptions}
                  value={selectedSession}
                  onChange={handleSessionChange}
                  onMenuScrollToBottom={handleScrollToBottom}
                  isLoading={isLoadingSessions}
                  placeholder="Select a session..."
                />
                <ErrorMsg msg={errors.sessionId} />
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Duration (Minutes) <span className="text-danger">*</span></label>
                    <Select
                      options={minuteOptions}
                      value={selectedMinute}
                      onChange={handleMinuteChange}
                      placeholder="Select Minutes"
                      isSearchable
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Duration (Seconds) <span className="text-danger">*</span></label>
                    <Select
                      options={secondOptions}
                      value={selectedSecond}
                      onChange={handleSecondChange}
                      placeholder="Select Seconds"
                      isSearchable
                    />
                  </div>
                </div>
              </div>
              <div className="row px-3 mt-n2 mb-3">
                <ErrorMsg msg={errors.duration} />
              </div>

              <div className="form-group">
                <label>Description <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  name="description"
                  value={description}
                  onChange={handleChange}
                  rows="4"
                />
                <ErrorMsg msg={errors.description} />
              </div>

              <div className="form-group">
                <label>Upload Video <span className="text-danger">*</span></label>
                <FileUploader
                  file={videoFile || (Url ? Url : null)}
                  setFile={setVideoFile}
                  dragText="Drop video file here or click to upload"
                  heading="Select Video File"
                  accept=".mp4, video/*"
                />
                {previewVideoUrl && (
                  <div className="mt-3 bg-light p-3 rounded" style={{ border: '1px dotted #ccc' }}>
                    <label className="font-weight-bold">Video Preview</label>
                    <ReactPlayer
                      url={previewVideoUrl}
                      width="100%"
                      height="250px"
                      controls={true}
                      className="mt-2"
                    />
                  </div>
                )}
                <ErrorMsg msg={errors.Url} />
              </div>

              <div className="form-group">
                <label>Thumbnail Image <span className="text-danger">*</span></label>
                <small className="d-block mb-2" style={{ color: "#666" }}>
                  <strong>Accepted formats:</strong> JPG, JPEG, PNG
                </small>
                <input
                  type="file"
                  className="form-control"
                  onChange={handleImageChange}
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                />
                {previewImage && (
                  <div className="mt-2">
                    <img
                      src={previewImage}
                      alt="Preview"
                      style={{ width: "150px", height: "auto" }}
                    />
                  </div>
                )}
                <ErrorMsg msg={errors.thumbnailImage} />
              </div>

              <button type="submit" className="btn btn-primary mt-3">
                {id ? "Update" : "Create"}
              </button>
              {id && (
                <button type="button" className="btn btn-secondary mt-3 ml-2" onClick={() => history.push("/dashboard/recorded-sessions/all")}>
                  Cancel
                </button>
              )}
            </form>
          </div>
          {alert}
        </div>
      )}
    </>
  );
};

export default CreateRecordedSession;
